import React, { forwardRef, createContext, useContext } from 'react';
import classNames from 'classnames';

// Form Context
interface FormContextValue {
  layout: 'horizontal' | 'vertical' | 'inline';
  labelWidth?: number | string;
  size?: 'sm' | 'md' | 'lg';
}

const FormContext = createContext<FormContextValue>({
  layout: 'vertical',
});

// Form
export interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  layout?: 'horizontal' | 'vertical' | 'inline';
  labelWidth?: number | string;
  size?: 'sm' | 'md' | 'lg';
}

const Form = forwardRef<HTMLFormElement, FormProps>(
  (
    {
      children,
      className,
      layout = 'vertical',
      labelWidth,
      size = 'md',
      ...rest
    },
    ref
  ) => {
    return (
      <FormContext.Provider value={{ layout, labelWidth, size }}>
        <form
          ref={ref}
          className={classNames(
            'ecme-form',
            `ecme-form-${layout}`,
            className
          )}
          {...rest}
        >
          {children}
        </form>
      </FormContext.Provider>
    );
  }
);

Form.displayName = 'Form';

// FormItem
export interface FormItemProps {
  label?: React.ReactNode;
  labelWidth?: number | string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  extra?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  layout?: 'horizontal' | 'vertical';
}

export const FormItem: React.FC<FormItemProps> = ({
  label,
  labelWidth: propLabelWidth,
  htmlFor,
  required,
  error,
  extra,
  className,
  children,
  layout: propLayout,
}) => {
  const context = useContext(FormContext);
  const layout = propLayout || context.layout;
  const labelWidth = propLabelWidth || context.labelWidth;

  const labelStyle =
    layout === 'horizontal' && labelWidth
      ? {
          width: typeof labelWidth === 'number' ? `${labelWidth}px` : labelWidth,
          minWidth: typeof labelWidth === 'number' ? `${labelWidth}px` : labelWidth,
        }
      : undefined;

  return (
    <div
      className={classNames(
        'ecme-form-item',
        { 'ecme-form-item-horizontal': layout === 'horizontal' },
        { 'ecme-form-item-error': !!error },
        className
      )}
    >
      {label && (
        <label
          htmlFor={htmlFor}
          className={classNames('ecme-form-label', { required })}
          style={labelStyle}
        >
          {label}
          {required && <span className="ecme-form-required">*</span>}
        </label>
      )}
      <div className="ecme-form-control">
        {children}
        {error && <div className="ecme-form-error">{error}</div>}
        {extra && <div className="ecme-form-extra">{extra}</div>}
      </div>
    </div>
  );
};

// FormContainer
export interface FormContainerProps extends React.HTMLAttributes<HTMLDivElement> {}

export const FormContainer: React.FC<FormContainerProps> = ({
  children,
  className,
  ...rest
}) => {
  return (
    <div className={classNames('ecme-form-container', className)} {...rest}>
      {children}
    </div>
  );
};

export default Form;
